import React, { useState } from "react";
import Logo from "../../assets/img/logocare-3.png";
import facebook from "../../assets/icons/facebook.svg";
import linkdin from "../../assets/icons/Linkedin.svg";
import twitter from "../../assets/icons/Twitter.svg";
import { Link } from "react-router-dom";

const footerData = {
	contact: {
		phone: "+1 (888) 569-9764",
		email: "support@synergycarefoundation.org",
	},
	quickLinks: [
		{ name: "Home", path: "/" },
		{ name: "Services", path: "/services" },
		{ name: "Impact", path: "/impact" },
		{ name: "Donate", path: "/donate" },
		// { name: "News & Event", path: "/news-event" },
		{ name: "Privacy Policy", path: "/privacy-policy" },
		{ name: "Contact Us", path: "/contact" },
		{ name: "Get Involved", path: "/get-involved" },
	],
	socials: [
		{ name: "LinkedIn", icon: linkdin, path: "#" },
		{ name: "Facebook", icon: facebook, path: "#" },
		{ name: "Twitter", icon: twitter, path: "#" },
	],
	copyright: "© 2025 SynergyCare Foundation. All rights reserved.",
};

const Footer = () => {
	// Add state for form data and validation
	const [formData, setFormData] = useState({
		email: "",
		consent: false,
	});
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitMessage, setSubmitMessage] = useState("");

	// Email validation function
	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Handle input changes
	const handleInputChange = (e) => {
		const { name, value, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: name === "consent" ? checked : value,
		}));
		// Clear errors when user types
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate form
		const newErrors = {};
		if (!formData.email) {
			newErrors.email = "Email is required";
		} else if (!validateEmail(formData.email)) {
			newErrors.email = "Please enter a valid email";
		}
		if (!formData.consent) {
			newErrors.consent = "You must accept the privacy policy";
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await fetch(
				"https://office.synergycarefoundation.org/api/newsletter",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						email: formData.email,
						acceptPrivacyPolicy: formData.consent,
					}),
				}
			);

			const data = await response.json();
			if (data.message === "Newsletter subscription successful") {
				setSubmitMessage("Thank you for subscribing!");
				setFormData({ email: "", consent: false });
			} else {
				throw new Error();
			}
		} catch (error) {
			setSubmitMessage("Failed to subscribe. Please try again later.");
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<footer>
			{/* First Section */}
			<div className='bg-[#FAFAFA] py-8'>
				<div className='max-w-[1440px] w-[90%] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 justify-between'>
					{/* Title and Description */}
					<div>
						<h3 className='text-2xl font-semibold text-[#11112D] mb-2'>
							Sign up for our newsletter
						</h3>
						<p className='text-[#43474F] text-base'>
							Be the first to know about any updates, industry news, and
							insights.
						</p>
					</div>

					{/* Newsletter Form */}
					<div>
						<form onSubmit={handleSubmit} className='flex flex-col gap-3'>
							<div className='flex flex-col md:flex-row gap-3'>
								<div className='flex-1'>
									<input
										type='email'
										name='email'
										value={formData.email}
										onChange={handleInputChange}
										placeholder='Enter your email'
										className={`w-full px-3 py-3 border rounded-full border-[#D0D5DD] focus:outline-none focus:ring-1 focus:ring-[#22E27F] ${
											errors.email ? "border-red-500" : ""
										}`}
									/>
									{errors.email && (
										<p className='text-red-500 text-sm mt-1 ml-3'>
											{errors.email}
										</p>
									)}
								</div>
								{/* Consent Checkbox */}
								<label className='flex md:hidden items-start gap-2 text-[#43474F] text-sm'>
									<input
										type='checkbox'
										name='consent'
										checked={formData.consent}
										onChange={handleInputChange}
										className={`mt-1 border-gray-300 focus:ring-[#22E27F] ${
											errors.consent ? "border-red-500" : ""
										}`}
									/>
									<span>
										I consent to the use of my information as outlined in the{" "}
										<Link
											to='/privacy-policy'
											className='text-[#22E27F] hover:text-[#1AD67E] transition duration-300'>
											Privacy Policy
										</Link>
										.
									</span>
								</label>
								{errors.consent && (
									<p className='text-red-500 text-sm ml-6'>{errors.consent}</p>
								)}
								<button
									type='submit'
									disabled={isSubmitting}
									className='bg-[#22E27F] text-black px-6 py-2 ml-3 rounded-full hover:bg-[#1AD67E] transition duration-300 disabled:opacity-50'>
									{isSubmitting ? "Subscribing..." : "Subscribe"}
								</button>
							</div>

							{/* Consent Checkbox */}
							<label className='md:flex hidden items-start gap-2 text-[#43474F] text-sm'>
								<input
									type='checkbox'
									name='consent'
									checked={formData.consent}
									onChange={handleInputChange}
									className={`mt-1 border-gray-300 focus:ring-[#22E27F] ${
										errors.consent ? "border-red-500" : ""
									}`}
								/>
								<span>
									I consent to the use of my information as outlined in the{" "}
									<Link
										to='/privacy-policy'
										className='text-[#22E27F] hover:text-[#1AD67E] transition duration-300'>
										Privacy Policy
									</Link>
									.
								</span>
							</label>
							{errors.consent && (
								<p className='text-red-500 text-sm ml-6'>{errors.consent}</p>
							)}

							{submitMessage && (
								<p
									className={`text-sm ml-3 ${
										submitMessage.includes("error")
											? "text-red-500"
											: "text-green-500"
									}`}>
									{submitMessage}
								</p>
							)}
						</form>
					</div>
				</div>
			</div>

			<div className='bg-[#F6FEFA]'>
				<div className='max-w-[1440px] w-[90%] mx-auto py-8 space-y-8'>
					{/* First Row */}
					<div className='flex items-center sm:justify-between justify-center gap-6 flex-wrap'>
						{/* Column 1: Logo, Number, and Email */}
						<div>
							<img src={Logo} alt='Logo' className='h-20' />
						</div>

						{/* Column 2: Quick Links */}
						<div className='flex flex-col gap-4'>
							<ul className='flex flex-wrap gap-4'>
								{footerData.quickLinks.map((link, index) => (
									<li key={index}>
										<a
											href={link.path}
											className='text-[#11112D] hover:text-[#22E27F] transition duration-300'>
											{link.name}
										</a>
									</li>
								))}
							</ul>
							<div className='flex gap-6 items-center'>
								<p className=' text-[#0A142F] text-lg font-normal'>
									{footerData.contact.phone}
								</p>
							</div>
						</div>
					</div>
					<hr />
					{/* Second Row */}
					<div className='flex sm:justify-between justify-center items-center gap-y-6 flex-wrap'>
						{/* Social Media Links */}
						<div className='flex space-x-4'>
							{footerData.socials.map((social, index) => (
								<a
									key={index}
									href={social.path}
									className='text-gray-600 hover:text-[#45E99E] transition duration-300'
									aria-label={social.name}>
									<img
										src={social.icon}
										alt={social.name}
										className='h-6 w-6' // You can adjust the size
									/>
								</a>
							))}
						</div>
						<p className='text-[#0A142F] text-base font-normal'>
							{footerData.contact.email}
						</p>
						{/* Copyright */}
						<div className='text-[#0A142F] font-normal text-base'>
							{footerData.copyright}
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
